import { computed } from 'vue'
import { yup } from '@/lib/validation'
import { parsePhoneNumberFromString } from 'libphonenumber-js'
import type {
  QuestionnaireQuestionTagEnum,
  QuestionnaireQuestionTypeEnum,
  FormFieldTypeEnum
} from '@/types/generated/types.gen'
import * as schemas from '@/types/generated/schemas.gen'

// Import the actual schemas from the generated file
import { $PatchedCustomerEntityRequest } from '@/types/generated/schemas.gen'

// Base validation rules for field types using existing validation methods
const FIELD_TYPE_VALIDATION_RULES: Partial<
  Record<QuestionnaireQuestionTypeEnum | FormFieldTypeEnum, any>
> = {
  textinput: {
    maxLength: 255,
    message: 'Bitte geben Sie maximal 255 Zeichen ein.'
  },
  textarea: {
    maxLength: 1000,
    message: 'Bitte geben Sie maximal 1000 Zeichen ein.'
  },
  emailinput: {
    maxLength: 128,
    message: 'Bitte geben Sie eine gültige E-Mail-Adresse ein (z.B. max.mustermann@beispiel.de).'
  },
  phoneinput: {
    maxLength: 128,
    message: 'Bitte geben Sie eine gültige Telefonnummer ein (z.B. +49 30 12345678).'
  },
  numberinput: {
    pattern: /^[0-9]+$/,
    message: 'Bitte geben Sie nur positive ganze Zahlen ein.'
  },
  decimalnumberinput: {
    pattern: /^-?\d{1,8}([.,]\d{1,2})?$/,
    message:
      'Bitte geben Sie eine gültige Dezimalzahl ein (max. 8 Ziffern vor dem Komma, 2 nach dem Komma, z.B. 123,45).'
  },
  urlinput: {
    maxLength: 200,
    message: 'Bitte geben Sie eine gültige URL ein (z.B. https://www.beispiel.de).'
  },
  datefield: {
    format: 'date',
    message: 'Bitte geben Sie ein gültiges Datum im Format DD.MM.YYYY ein (z.B. 31.12.2023).'
  },
  timefield: {
    pattern: /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/,
    message: 'Bitte geben Sie eine gültige Uhrzeit im Format HH:MM ein (z.B. 14:30).'
  },
  select: {
    message: 'Bitte wählen Sie eine gültige Option aus der Liste aus.'
  },
  multiselect: {
    message: 'Bitte wählen Sie mindestens eine Option aus der Liste aus.'
  },
  yesno: {
    message: 'Bitte wählen Sie Ja oder Nein aus.'
  },
  filefield: {
    message: 'Bitte wählen Sie eine Datei zum Hochladen aus.'
  },
  multifilefield: {
    message: 'Bitte wählen Sie mindestens eine Datei zum Hochladen aus.'
  },
  checkbox: {
    message: 'Bitte setzen Sie das Häkchen für dieses Feld.'
  }
}

export interface ValidationRule {
  maxLength?: number
  minLength?: number
  pattern?: RegExp
  message: string
  required?: boolean
  format?: string
  nullable?: boolean
  oneOf?: any[] // Added for oneOf constraint
}

export interface FieldValidation {
  tag?: QuestionnaireQuestionTagEnum | string | null
  tag_relationship?: string
  tag_number?: number
  type: QuestionnaireQuestionTypeEnum | FormFieldTypeEnum
  optional?: boolean
  choices?: string[]
}

export function getValidationRule(field: FieldValidation): ValidationRule {
  if (field.tag && field.tag.startsWith('entities.field.')) {
    const schemaField = field.tag.split('entities.field.')[1]
    const schemaProperties = $PatchedCustomerEntityRequest.properties as any

    if (schemaProperties[schemaField]) {
      const fieldSchema = schemaProperties[schemaField]

      // Generate more descriptive error messages based on field type and constraints
      const message = generateFieldErrorMessage(fieldSchema)

      const rule: ValidationRule = {
        message
      }

      if (fieldSchema.maxLength) {
        rule.maxLength = fieldSchema.maxLength
      }

      if (fieldSchema.format) {
        rule.format = fieldSchema.format
      }
      if (fieldSchema.nullable) {
        rule.nullable = fieldSchema.nullable
      }

      if (fieldSchema.oneOf) {
        rule.oneOf = fieldSchema.oneOf
      }

      return rule
    }
  }

  if (FIELD_TYPE_VALIDATION_RULES[field.type]) {
    return FIELD_TYPE_VALIDATION_RULES[field.type]
  }

  return {
    maxLength: 255,
    message: 'Ungültige Eingabe.'
  }
}

function generateFieldErrorMessage(fieldSchema: any): string {
  const fieldName = fieldSchema.title || 'Feld'
  return `${fieldName} ist ungültig oder fehlt.`
}

export function createValidationSchema(field: FieldValidation): any {
  const rule = getValidationRule(field)

  // Choose the correct base schema type
  let schema: any
  if (
    field.type === 'multiselect' ||
    field.type === 'filefield' ||
    field.type === 'multifilefield'
  ) {
    schema = yup.array()
  } else if (field.type === 'yesno' || field.type === 'checkbox') {
    schema = yup.boolean()
  } else {
    schema = yup.string()
  }

  // Handle required/optional based on schema
  if (!field.optional && !rule.nullable) {
    if (field.type === 'select' && field.choices?.includes('')) {
      schema = schema.nullable()
    } else {
      schema = schema.required('Dieses Feld ist erforderlich.')
    }
  }

  if (
    rule.maxLength &&
    field.type !== 'multiselect' &&
    field.type !== 'filefield' &&
    field.type !== 'multifilefield'
  ) {
    schema = schema.max(rule.maxLength, rule.message)
  }

  if (
    rule.minLength &&
    field.type !== 'multiselect' &&
    field.type !== 'filefield' &&
    field.type !== 'multifilefield'
  ) {
    schema = schema.min(rule.minLength, rule.message)
  }

  if (
    rule.pattern &&
    field.type !== 'multiselect' &&
    field.type !== 'filefield' &&
    field.type !== 'multifilefield'
  ) {
    schema = schema.matches(rule.pattern, rule.message)
  }

  // Handle oneOf constraint for schema fields with predefined options
  if (
    rule.oneOf &&
    field.type !== 'multiselect' &&
    field.type !== 'filefield' &&
    field.type !== 'multifilefield'
  ) {
    // Extract the actual values from the oneOf array
    let choices: any[] = []

    choices = rule.oneOf
      .map((option: any) => {
        if (option.$ref) {
          const refPath = option.$ref
          if (refPath.startsWith('#/components/schemas/')) {
            const schemaName = refPath.replace('#/components/schemas/', '')
            const schemaKey = `$${schemaName}` as keyof typeof schemas
            const schemaObject = schemas[schemaKey]

            if (schemaObject && typeof schemaObject === 'object' && 'enum' in schemaObject) {
              return schemaObject.enum
            }
          }
          return null
        }
        return option
      })
      .filter((val) => val !== null && val !== undefined)
      .flat()

    if (choices.length > 0) {
      schema = schema.oneOf(choices, rule.message)
    }
  }

  switch (field.type) {
    case 'emailinput':
      schema = schema.email(rule.message)
      break
    case 'phoneinput':
      schema = schema.test('phone', rule.message, (value: any) => {
        if (!value) return true
        try {
          const phoneNumber = parsePhoneNumberFromString(value)
          if (phoneNumber && phoneNumber.isValid()) {
            return true
          }

          const germanPhoneRegex = /^(\+?49|0)?[0-9\s\-()]{6,15}$/
          if (germanPhoneRegex.test(value.replace(/\s/g, ''))) {
            return true
          }

          return false
        } catch (error) {
          return false
        }
      })
      break
    case 'numberinput':
      schema = schema.test('is-number', rule.message, (value: any) => {
        if (!value) return true
        return !isNaN(Number(value)) && Number.isInteger(Number(value))
      })
      break
    case 'decimalnumberinput':
      schema = schema.test('is-decimal', rule.message, (value: any) => {
        if (!value) return true
        // Convert comma to dot for number parsing
        const normalizedValue = value.replace(',', '.')
        const numValue = Number(normalizedValue)
        return !isNaN(numValue) // Allow negative numbers
      })
      break
    case 'datefield':
      if (rule.format === 'date') {
        schema = schema.test('is-valid-date', rule.message, (value: any) => {
          if (!value) return true
          // Accept both DD.MM.YYYY and YYYY-MM-DD formats
          const dateRegex =
            /^(0[1-9]|[12][0-9]|3[01])\.(0[1-9]|1[0-2])\.(19|20)\d{2}$|^\d{4}-\d{2}-\d{2}$/
          if (!dateRegex.test(value)) return false

          const [day, month, year] = value.includes('.')
            ? value.split('.').map(Number)
            : value.split('-').reverse().map(Number)

          const date = new Date(year, month - 1, day)
          return (
            date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day
          )
        })
      } else {
        // Fallback to DD.MM.YYYY format
        schema = schema.test('is-valid-date', rule.message, (value: any) => {
          if (!value) return true // Allow empty if optional
          const [day, month, year] = value.split('.').map(Number)
          const date = new Date(year, month - 1, day)
          return (
            date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day
          )
        })
      }
      break
    case 'yesno':
    case 'checkbox':
      schema = schema.test('is-boolean', rule.message, (value: any) => {
        if (field.optional && (value === null || value === undefined || value === '')) return true
        return value === true || value === false
      })
      break
    case 'multiselect':
      schema = schema.test('is-array', rule.message, (value: any) => {
        // Allow empty arrays for optional fields
        if (field.optional && (!value || (Array.isArray(value) && value.length === 0))) {
          return true
        }
        // For required fields, require at least one selection
        return Array.isArray(value) && value.length > 0
      })
      break
    case 'urlinput':
      schema = schema.url(rule.message)
      break
    case 'filefield':
    case 'multifilefield':
      schema = schema.test('has-files', rule.message, (value: any) => {
        if (!value) return true // Allow empty if optional
        return Array.isArray(value) && value.length > 0
      })
      break
  }

  return schema
}

export function validateField(
  field: FieldValidation,
  value: any
): { valid: boolean; errors: string[] } {
  try {
    const schema = createValidationSchema(field)
    schema.validateSync(value)
    return { valid: true, errors: [] }
  } catch (error) {
    if (error instanceof yup.ValidationError) {
      return { valid: false, errors: error.errors }
    }
    return { valid: false, errors: ['Ungültige Eingabe.'] }
  }
}

export function useFieldValidation(field: FieldValidation) {
  const validationRule = computed(() => getValidationRule(field))
  const validationSchema = computed(() => createValidationSchema(field))

  return {
    validationRule,
    validationSchema,
    validate: (value: any) => validateField(field, value)
  }
}
